import { LogInPage } from "@/pages/log-in";
import { LandingPage } from "@/pages/landing";
import { RouteObject } from "react-router-dom";
import { LogOutPage } from "@/pages/log-out";
import { GoogleOAuthRedirectPage } from "@/pages/google-oauth-redirect";
import { GoogleOAuthRedirectDesktopPage } from "@/pages/google-oauth-redirect-desktop";
import { RootOutlet } from "@/app/outlets/RootOutlet";
import { SignUpPage } from "@/pages/sign-up";
import { StoreProviderOutlet } from "@/app/outlets/StoreProviderOutlet";
import { Navigate } from "react-router-dom";
import { GuestNotesViewPage, StandardNotesViewPage } from "@/pages/notes-view/NotesViewPage";
import { StandardModeAppLayoutOutlet } from "@/app/outlets/layout/StandardModeAppLayoutOutlet";
import { AdminPage } from "@/pages/admin";
import { RequireAdminAuthOutlet } from "@/app/outlets/AdminOutlet";
import { TrashPage } from "@/pages/trash";
import { CollectionsListPage } from "@/pages/collections";
import { CollectionsViewPage } from "@/pages/collections-view";
import { IntegrationsPage, IntegrationsApiPage } from "@/pages/integrations";
import { SearchPage } from "@/pages/search";
import { SettingsLayoutOutlet } from "@/app/outlets/SettingsLayoutOutlet";
import {
  SettingsEmailPreferencesPage,
  SettingsAccountManagementPage,
  SettingsSubscriptionManagementPage,
  SettingsExportsPage,
  SettingsDebugAccountPage,
} from "@/pages/settings";
import { NotFoundPage } from "@/pages/not-found";
import { appRoutes } from "@/app/router/appRoutes";
import { PageElementRouter } from "@/app/outlets/PageElementRouter";
import { RequireStandardModeOutlet } from "@/app/outlets/RequireStandardModeOutlet";
import { RequireGuestModeOrStandardModeOutlet } from "@/app/outlets/RequireGuestModeOrStandardModeOutlet";
import { GuestModeAppLayoutOutlet } from "@/app/outlets/layout/GuestModeAppLayoutOutlet";
import { LogInEmailPasswordPage } from "@/pages/log-in-email-password";
import { StandardModePadding } from "@/app/outlets/StandardModePadding";
import { NotesListPage } from "@/pages/notes/NotesListPage";
import { SettingsAccountMigrationPage } from "@/pages/settings/SettingsAccountMigrationPage";
import { SettingsDebugSandboxPage } from "@/pages/settings/SettingsDebugSandboxPage";
import { ExplorePage } from "@/pages/explore/ExplorePage";
import TopicDetailPage from "@/pages/explore/TopicDetailPage";
import { LogInOAuthTokenPage } from "@/pages/log-in-oauth-token";
import { ChatConversationPage } from "@/pages/chat";
import { TemplatesListPage } from "@/pages/templates-list/TemplatesListPage";
import { TemplatesViewPage } from "@/pages/templates-view/TemplatesViewPage";
import { V1MigrationPage } from "@/pages/v1-migration/V1MigrationPage";
import { SessionsListPage } from "@/pages/sessions-list";
import { SessionsViewPage } from "@/pages/sessions-view";
import { IntegrationsEmailPage } from "@/pages/integrations/email/integrations-email-page";
import { IntegrationsTextMessagePage } from "@/pages/integrations/text-message/integrations-text-message-page";
import { OAuthConsentPage } from "@/pages/oauth-consent/oauth-consent-page";
import { IntegrationsZapierPage } from "@/pages/integrations/zapier/integrations-zapier-page";
import { ImportedNotesListPage } from "@/pages/notes/ImportedNotesListPage";
import { ImportedCollectionsListPage } from "@/pages/collections/ImportedCollectionsListPage";

const GUEST_MODE_OR_STANDARD_MODE_ROUTES: RouteObject[] = [
  /**
   * =============================================
   *         Notes Routes
   * =============================================
   */
  {
    path: appRoutes.notesView({
      params: { noteId: ":noteId" },
    }).path,
    element: (
      <PageElementRouter
        standardModeElement={<StandardNotesViewPage />}
        guestModeElement={<GuestNotesViewPage />}
      />
    ),
  },
];

export const STANDARD_MODE_ROUTES: RouteObject[] = [
  {
    path: appRoutes.root({}).path,
    element: <RequireStandardModeOutlet />,
    children: [
      /**
       * =============================================
       *               V1 Migrated Routes
       * =============================================
       */
      {
        path: appRoutes.v1MigrationGroups({ starPattern: true }).path,
        element: <V1MigrationPage />,
      },
      {
        path: appRoutes.v1MigrationNotes({ starPattern: true }).path,
        element: <V1MigrationPage />,
      },
      {
        path: appRoutes.v1MigrationTemplates({ starPattern: true }).path,
        element: <V1MigrationPage />,
      },
      /**
       * =============================================
       *               OAuth Routes
       * =============================================
       */
      {
        path: appRoutes.oauthConsent({}).path,
        element: <OAuthConsentPage />,
      },
      /**
       * =============================================
       *               Notes Routes
       * =============================================
       */
      {
        path: appRoutes.notesList({}).path,
        element: <NotesListPage />,
      },
      {
        path: appRoutes.home({}).path,
        element: <Navigate to={appRoutes.notesList({}).path} />,
      },
      {
        path: appRoutes.everything({}).path,
        element: <Navigate to={appRoutes.notesList({}).path} />,
      },
      /**
       * =============================================
       *               Import Routes
       * =============================================
       */
      {
        path: appRoutes.importBatchNotes({
          params: { importBatchId: ":importBatchId" },
        }).path,
        element: <ImportedNotesListPage />,
      },
      {
        path: appRoutes.importBatchCollections({
          params: { importBatchId: ":importBatchId" },
        }).path,
        element: <ImportedCollectionsListPage />,
      },
      /**
       * =============================================
       *               Trash Routes
       * =============================================
       */
      {
        path: appRoutes.trash({}).path,
        element: <TrashPage />,
      },
      /**
       * =============================================
       *               Session Routes
       * =============================================
       */
      {
        path: appRoutes.sessionsList({}).path,
        element: <SessionsListPage />,
      },
      {
        path: appRoutes.sessionsView({
          params: { sessionId: ":sessionId" },
        }).path,
        element: <SessionsViewPage />,
      },
      /**
       * =============================================
       *               Settings Routes
       * =============================================
       */
      {
        path: appRoutes.settings({}).path,
        element: <SettingsLayoutOutlet />,
        children: [
          {
            path: appRoutes.settingsEmailPreferences({}).path,
            element: <SettingsEmailPreferencesPage />,
          },
          {
            path: appRoutes.settingsSubscriptionManagement({}).path,
            element: <SettingsSubscriptionManagementPage />,
          },
          {
            path: appRoutes.settingsAccountManagement({}).path,
            element: <SettingsAccountManagementPage />,
          },
          {
            path: appRoutes.settingsExports({}).path,
            element: <SettingsExportsPage />,
          },
          {
            path: appRoutes.settingsAccountMigration({}).path,
            element: <SettingsAccountMigrationPage />,
          },
          {
            path: appRoutes.settingsDebugAccount({}).path,
            element: <SettingsDebugAccountPage />,
          },
          {
            path: appRoutes.settingsDebugSandbox({}).path,
            element: <SettingsDebugSandboxPage />,
          },
          {
            path: appRoutes.settingsDebug({}).path,
            element: <Navigate to={appRoutes.settingsDebugAccount({}).path} />,
          },
          {
            path: appRoutes.settings({}).path,
            element: <Navigate to={appRoutes.settingsEmailPreferences({}).path} />,
          },
        ],
      },
      /**
       * =============================================
       *               Chat Routes
       * =============================================
       */
      {
        path: appRoutes.chat({}).path,
        element: <ChatConversationPage />,
      },
      {
        path: appRoutes.chatConversation({
          params: { conversationId: ":conversationId" },
        }).path,
        element: <ChatConversationPage />,
      },
      /**
       * =============================================
       *               Explore Routes
       * =============================================
       */
      {
        path: appRoutes.explore({}).path,
        element: <ExplorePage />,
      },
      {
        path: appRoutes.exploreTopicDetail({
          params: { topicId: ":topicId" },
        }).path,
        element: <TopicDetailPage />,
      },
      /**
       * =============================================
       *               Collections Routes
       * =============================================
       */
      {
        path: appRoutes.collections({}).path,
        element: <CollectionsListPage />,
      },
      {
        path: appRoutes.collectionsView({
          params: { collectionId: ":collectionId" },
        }).path,
        element: <CollectionsViewPage />,
      },
      /**
       * =============================================
       *               Templates Routes
       * =============================================
       */
      {
        path: appRoutes.templatesList({}).path,
        element: <TemplatesListPage />,
      },
      {
        path: appRoutes.templatesView({
          params: { templateId: ":templateId" },
        }).path,
        element: <TemplatesViewPage />,
      },
      /**
       * =============================================
       *               Integrations Routes
       * =============================================
       */
      {
        path: appRoutes.integrations({}).path,
        element: <IntegrationsPage />,
      },

      {
        path: appRoutes.integrationsApi({}).path,
        element: <IntegrationsApiPage />,
      },

      {
        path: appRoutes.integrationsZapier({}).path,
        element: <IntegrationsZapierPage />,
      },

      {
        path: appRoutes.integrationsEmail({}).path,
        element: <IntegrationsEmailPage />,
      },

      {
        path: appRoutes.integrationsTextMessage({}).path,
        element: <IntegrationsTextMessagePage />,
      },

      /**
       * =============================================
       *               Search Routes
       * =============================================
       */
      {
        path: appRoutes.search({}).path,
        element: <SearchPage />,
      },
      /**
       * =============================================
       *               Admin Routes
       * =============================================
       */
      {
        path: appRoutes.admin({}).path,
        element: <RequireAdminAuthOutlet />,
        children: [
          {
            path: appRoutes.admin({}).path,
            element: <AdminPage />,
          },
        ],
      },
    ],
  },
  {
    path: appRoutes.root({ starPattern: true }).path,
    element: (
      // Apply consistent padding for the NotFoundPage.
      <StandardModePadding>
        <NotFoundPage />
      </StandardModePadding>
    ),
  },
];

export const ALL_APP_ROUTES: RouteObject[] = [
  {
    path: appRoutes.root({}).path,
    element: <RootOutlet />,
    children: [
      /**
       * =============================================
       *                Public Routes
       * =============================================
       */
      {
        path: appRoutes.root({}).path,
        element: <LandingPage />,
      },
      {
        path: appRoutes.logInEmailPassword({}).path,
        element: <LogInEmailPasswordPage />,
      },
      {
        path: appRoutes.logInOAuthToken({}).path,
        element: <LogInOAuthTokenPage />,
      },
      {
        path: appRoutes.logIn({}).path,
        element: <LogInPage />,
      },
      {
        path: appRoutes.signUp({}).path,
        element: <SignUpPage />,
      },
      {
        path: appRoutes.logOut({}).path,
        element: <LogOutPage />,
      },
      {
        path: appRoutes.googleOAuthRedirect({}).path,
        element: <GoogleOAuthRedirectPage />,
      },
      {
        path: appRoutes.googleOAuthRedirectDesktop({}).path,
        element: <GoogleOAuthRedirectDesktopPage />,
      },

      {
        path: appRoutes.root({}).path,
        element: <StoreProviderOutlet />,
        children: [
          {
            path: appRoutes.root({}).path,
            element: <RequireGuestModeOrStandardModeOutlet />,
            children: [
              {
                path: appRoutes.root({}).path,
                element: (
                  <PageElementRouter
                    standardModeElement={<StandardModeAppLayoutOutlet />}
                    guestModeElement={<GuestModeAppLayoutOutlet />}
                  />
                ),
                children: [...GUEST_MODE_OR_STANDARD_MODE_ROUTES, ...STANDARD_MODE_ROUTES],
              },
            ],
          },
        ],
      },
    ],
  },
];
